// Google Tag Manager dataLayer helpers.
//
// GTM (container id in config/configAnalytics.js) is loaded in util/includeScripts.js.
// GA4 ecommerce + custom events are pushed to window.dataLayer here, following
// Google's GA4 ecommerce spec:
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm
//
// IMPORTANT: never push PII (email, name, etc.) to the dataLayer.

const isBrowser = () => typeof window !== 'undefined';

const PURCHASE_SENT_KEY = 'ga4_purchase_sent';

// Analytics must NEVER break the app. Every exported helper is wrapped with this
// so a malformed entity, a missing window, a hostile window.dataLayer value, or
// blocked storage can't bubble an exception into a render path, a checkout step,
// auth, or navigation. On failure we swallow and return a safe fallback.
const safe = (fn, fallback) => (...args) => {
  try {
    return fn(...args);
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[analytics] dataLayer helper failed:', e);
    }
    return typeof fallback === 'function' ? fallback() : fallback;
  }
};

// Sharetribe Money amounts are in minor units (e.g. cents).
const toMajorUnits = money =>
  money && typeof money.amount === 'number' ? money.amount / 100 : undefined;

const listingCurrency = listing => listing?.attributes?.price?.currency;

export const pushToDataLayer = safe(obj => {
  if (!isBrowser() || !obj) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
});

// Ecommerce events clear the previous `ecommerce` object before pushing the
// next one, so stale items don't leak between events (GA4 + GTM best practice).
export const pushEcommerceEvent = safe((event, ecommerce) => {
  if (!isBrowser()) {
    return;
  }
  pushToDataLayer({ ecommerce: null });
  pushToDataLayer({ event, ecommerce });
});

// Non-ecommerce events (login, sign_up, search, consent, ...). PII-free.
export const pushEvent = safe((event, params = {}) => {
  pushToDataLayer({ event, ...params });
});

// Map a Sharetribe listing entity to a GA4 `item`. Field names are best-effort
// against the current publicData taxonomy and easy to adjust.
export const listingToItem = safe((listing, { index, quantity } = {}) => {
  if (!listing) {
    return null;
  }
  const id = listing.id?.uuid || listing.id;
  const { title, price, publicData } = listing.attributes || {};
  const category = publicData?.listingType || publicData?.category;

  const item = {
    item_id: id,
    item_name: title,
    price: toMajorUnits(price),
  };
  if (publicData?.brandChild) {
    item.item_brand = publicData.brandChild;
  }
  if (category) {
    item.item_category = category;
  }
  if (publicData?.condition) {
    item.item_variant = publicData.condition;
  }
  if (typeof index === 'number') {
    item.index = index;
  }
  if (typeof quantity === 'number') {
    item.quantity = quantity;
  }
  return item;
}, null);

// Build the `ecommerce` payload for a single-listing event (view_item,
// select_to_buy, begin_checkout, add_payment_info). `value` is the sum of
// price * quantity and `currency` is taken from the listing, so each event is
// internally currency-consistent.
export const singleListingEcommerce = safe(
  (listing, options = {}) => {
    const { quantity = 1, index, itemListId, itemListName } = options;
    const item = listingToItem(listing, { quantity, index });
    if (!item) {
      return { items: [] };
    }
    if (itemListId) {
      item.item_list_id = itemListId;
    }
    if (itemListName) {
      item.item_list_name = itemListName;
    }
    return {
      currency: listingCurrency(listing),
      value: (item.price || 0) * (item.quantity || 1),
      items: [item],
    };
  },
  () => ({ items: [] })
);

// Build the `ecommerce` payload for view_item_list (capped at GA4's 200 items).
export const listingsToItemList = safe(
  (listings, options = {}) => {
    const { itemListId, itemListName } = options;
    const items = (listings || [])
      .slice(0, 200)
      .map((listing, index) => {
        const item = listingToItem(listing, { index });
        if (!item) {
          return null;
        }
        if (itemListId) {
          item.item_list_id = itemListId;
        }
        if (itemListName) {
          item.item_list_name = itemListName;
        }
        return item;
      })
      .filter(Boolean);
    return { item_list_id: itemListId, item_list_name: itemListName, items };
  },
  () => ({ items: [] })
);

// Build the GA4 `purchase` ecommerce object from the completed order and the
// checkout page data. Used by both purchase call sites (card and SEPA/Klarna)
// so they can't drift. Revenue uses the order's actual payinTotal.
export const orderToPurchasePayload = safe(
  (order, pageData) => {
    const payinTotal = order?.attributes?.payinTotal;
    const listing = pageData?.listing;
    const quantity = pageData?.orderData?.quantity || 1;
    const item = listingToItem(listing, { quantity });
    return {
      transaction_id: order?.id?.uuid,
      value: toMajorUnits(payinTotal),
      currency: payinTotal?.currency,
      items: item ? [item] : [],
    };
  },
  () => ({ items: [] })
);

// Returns true at most once per transaction id (per browser session), so the
// `purchase` event is sent exactly once across payment paths and remounts.
// On any unexpected failure we return false (skip the event) rather than risk
// blocking the post-payment flow or sending a duplicate.
export const oncePerTransaction = safe(transactionId => {
  if (!isBrowser() || !transactionId) {
    return false;
  }
  try {
    const raw = window.sessionStorage.getItem(PURCHASE_SENT_KEY);
    const sent = raw ? JSON.parse(raw) : [];
    if (sent.includes(transactionId)) {
      return false;
    }
    sent.push(transactionId);
    window.sessionStorage.setItem(PURCHASE_SENT_KEY, JSON.stringify(sent));
    return true;
  } catch (e) {
    // If sessionStorage is unavailable, don't block the event.
    return true;
  }
}, false);
